/**
 * v3: Profile sync + selected allergy/dislike IDs for menu health.
 *
 * Combined (`allergy_picker_mode=combined`): one inventory pool → allergy checks
 * (dislike list empty for classification; recipe dislike_id fields still match via healthCode).
 * Split: same legacy GET sources as the allergies/dislikes picker (not a separate catalog
 * that can go stale after save).
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '../../../shared/auth/store';
import { fetchProfileAndSyncUser } from '../profile/api';
import type { Allergy, Dislike } from '../profile/types';
import { useAllergyPickerMode } from '../config';
import {
  PROFILE_ALLERGIES_DISLIKES_QUERY_KEY,
  usePreferencesCatalog as useAllergyDislikesQuery,
  useSelectedInventoryIds,
} from './hooks';
import {
  useLegacySelectedAllergyIds,
  useLegacySelectedDislikeIds,
} from './legacyAllergiesHooks';
import { inventoryIdsFromProfile } from './inventoryPreferenceTypes';

export { PROFILE_ALLERGIES_DISLIKES_QUERY_KEY };

function parseNumericId(x: unknown): number | null {
  if (typeof x === 'number' && Number.isFinite(x)) {
    return x;
  }
  if (typeof x === 'string') {
    const n = Number(x);
    if (Number.isFinite(n)) {
      return n;
    }
  }
  return null;
}

function normalizeIdList(raw: unknown[]): number[] {
  const out: number[] = [];
  for (const x of raw) {
    const n = parseNumericId(x);
    if (n != null) {
      out.push(n);
    }
  }
  return out;
}

function allergyIdsFromProfile(user: { allergies?: any[]; allergy_ids?: number[] } | null): number[] {
  if (!user) {
    return [];
  }
  const raw_ids = user.allergy_ids;
  if (Array.isArray(raw_ids) && raw_ids.length > 0) {
    return normalizeIdList(raw_ids);
  }
  if (!user.allergies || !Array.isArray(user.allergies)) {
    return [];
  }
  const mapped = user.allergies.map((a: any) => (typeof a === 'number' ? a : a?.id ?? a?.allergy_id));
  return normalizeIdList(mapped);
}

/** Fetch profile and sync allergies/dislikes (and rest of profile) into auth store. */
export function useProfileAllergiesDislikesSync(customer_id?: number) {
  const store_customer_id = useAuthStore(s => s.user?.id);
  const resolved_id = customer_id ?? store_customer_id;

  return useQuery({
    queryKey: [...PROFILE_ALLERGIES_DISLIKES_QUERY_KEY, resolved_id],
    queryFn: () => fetchProfileAndSyncUser(resolved_id!),
    enabled: typeof resolved_id === 'number',
    staleTime: 1000 * 60 * 2,
  });
}

/**
 * Selected allergy IDs for menu health.
 * Combined: inventory ids from `allergy_dislikes` (profile inventory fallback while loading / on bad shape).
 * Split: legacy allergies GET (same as picker) with profile fallback inside that hook.
 */
export function useSelectedAllergyIds(): number[] {
  const allergy_picker_mode = useAllergyPickerMode();
  const user = useAuthStore(s => s.user);
  const customer_id = user?.id;
  const { isSuccess: selections_loaded } = useAllergyDislikesQuery(customer_id);
  const inventory_ids = useSelectedInventoryIds();
  const legacy_allergy_ids = useLegacySelectedAllergyIds();

  return useMemo(() => {
    if (allergy_picker_mode === 'combined') {
      if (selections_loaded && typeof customer_id === 'number') {
        return inventory_ids;
      }
      // Catalog not ready — keep menu warnings alive from profile inventory / allergy ids.
      const from_profile = inventoryIdsFromProfile(user);
      if (from_profile.length > 0) {
        return from_profile;
      }
      return allergyIdsFromProfile(user);
    }
    return legacy_allergy_ids;
  }, [
    allergy_picker_mode,
    user,
    selections_loaded,
    customer_id,
    inventory_ids,
    legacy_allergy_ids,
    user?.allergies,
    user?.allergy_ids,
    user?.inventory_ids,
    user?.dislike_ids,
  ]);
}

/**
 * Selected dislike IDs for menu health.
 * Combined: empty for UI classification (all inventory treated as allergies); matching still
 * finds recipe dislike_id tags via allergy id cross-field checks in healthCode.
 * Split: legacy dislikes GET (same as picker) with profile fallback inside that hook.
 */
export function useSelectedDislikeIds(): number[] {
  const allergy_picker_mode = useAllergyPickerMode();
  const legacy_dislike_ids = useLegacySelectedDislikeIds();

  return useMemo(() => {
    if (allergy_picker_mode === 'combined') {
      return [];
    }
    return legacy_dislike_ids;
  }, [allergy_picker_mode, legacy_dislike_ids]);
}

/** Full allergy objects from profile-backed auth store. */
export function useProfileAllergies(): Allergy[] {
  const allergies = useAuthStore(s => s.user?.allergies);
  return useMemo(() => {
    if (!Array.isArray(allergies)) {
      return [];
    }
    return allergies.filter((a): a is Allergy => a != null && typeof a.id === 'number');
  }, [allergies]);
}

/** Full dislike objects from profile-backed auth store. */
export function useProfileDislikes(): Dislike[] {
  const dislikes = useAuthStore(s => s.user?.dislikes);
  return useMemo(() => {
    if (!Array.isArray(dislikes)) {
      return [];
    }
    return dislikes.filter((d): d is Dislike => d != null && typeof d.id === 'number');
  }, [dislikes]);
}
